import { motion } from "framer-motion";
import { Code2, Layers, Coffee, Briefcase, Smartphone, Shield } from "lucide-react";
import { useLanguage } from "../i18n/LanguageProvider";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.1 },
  }),
};

const BentoCard = ({ children, className = "", index = 0, testId }) => (
  <motion.div
    custom={index}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-60px" }}
    variants={fadeUp}
    data-testid={testId}
    className={`relative overflow-hidden bg-[#0F0F10] border border-white/5 rounded-2xl p-8 card-shine border-glow ${className}`}
  >
    {children}
  </motion.div>
);

export const About = () => {
  const { t } = useLanguage();
  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section label */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-sm tracking-widest uppercase text-zinc-500 font-medium mb-4"
        >
          {t("about.label")}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-3xl md:text-4xl font-semibold tracking-tight text-white/90 mb-16"
        >
          {t("about.title")}
          <br />
          {t("about.titleLine2")}
        </motion.h2>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {/* Experience */}
          <BentoCard className="lg:col-span-2" index={0} testId="about-experience-card">
            <div className="flex items-start gap-4 mb-6">
              <div className="p-2.5 rounded-xl bg-white/5">
                <Briefcase size={20} className="text-zinc-300" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="text-lg font-medium text-white mb-1">{t("about.yearsTitle")}</h3>
                <p className="text-sm text-zinc-500">{t("about.yearsSubtitle")}</p>
              </div>
            </div>
            <p className="text-zinc-400 leading-relaxed mb-4">
              {t("about.yearsBody")}
            </p>
            <p className="text-zinc-400 text-sm">
              <span className="text-zinc-500">&#x21B3;</span> {t("about.yearsCurrent")}
            </p>
          </BentoCard>

          {/* Fintech */}
          <BentoCard index={1} testId="about-fintech-card">
            <div className="p-2.5 rounded-xl bg-white/5 w-fit mb-6">
              <Shield size={20} className="text-zinc-300" strokeWidth={1.5} />
            </div>
            <h3 className="text-lg font-medium text-white mb-2">{t("about.domainTitle")}</h3>
            <p className="text-sm text-zinc-400 leading-relaxed mb-4">
              {t("about.domainBody")}
            </p>
            <div className="flex flex-wrap gap-2">
              {["Fintech", "Digital Identity", "Cross-Border Payments", "Stock Trading", "Digital Lending", "Security Compliance"].map((domain) => (
                <span
                  key={domain}
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white/5 text-zinc-300 border border-white/5"
                >
                  {domain}
                </span>
              ))}
            </div>
          </BentoCard>

          {/* Ecosystem & Engineering */}
          <BentoCard index={2} testId="about-techstack-card">
            <div className="p-2.5 rounded-xl bg-white/5 w-fit mb-6">
              <Code2 size={20} className="text-zinc-300" strokeWidth={1.5} />
            </div>
            <h3 className="text-lg font-medium text-white mb-4">{t("about.stackTitle")}</h3>
            <div className="flex flex-wrap gap-2">
              {["Swift", "SwiftUI", "AppKit", "watchOS", "StoreKit 2", "Core ML", "LLM Interactions", "Fastlane", "Jenkins", "Mixpanel", "Firebase", "Dynatrace", "Netmera"].map((tech) => (
                <span
                  key={tech}
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white/5 text-zinc-300 border border-white/5"
                >
                  {tech}
                </span>
              ))}
            </div>
          </BentoCard>

          {/* Architecture & Leadership */}
          <BentoCard index={3} testId="about-architecture-card">
            <div className="p-2.5 rounded-xl bg-white/5 w-fit mb-6">
              <Layers size={20} className="text-zinc-300" strokeWidth={1.5} />
            </div>
            <h3 className="text-lg font-medium text-white mb-2">{t("about.architectureTitle")}</h3>
            <p className="text-sm text-zinc-400 leading-relaxed mb-4">
              {t("about.architectureBody")}
            </p>
            <div className="flex flex-wrap gap-2">
              {["MVVM", "VIPER", "Modular Architecture", "Agile Leadership"].map((arch) => (
                <span
                  key={arch}
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white/5 text-zinc-300 border border-white/5"
                >
                  {arch}
                </span>
              ))}
            </div>
          </BentoCard>

          {/* The Remote Craft */}
          <BentoCard index={5} testId="about-coffee-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 rounded-xl bg-white/5">
                <Coffee size={20} className="text-zinc-300" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-medium text-white">{t("about.remoteTitle")}</h3>
            </div>
            <p className="text-sm text-zinc-400 leading-relaxed">
              {t("about.remoteBody")}
            </p>
          </BentoCard>
        </div>
      </div>
    </section>
  );
};
